import React, { useState, useEffect, useRef, useCallback } from "react";
import { TbX, TbChevronUp, TbChevronDown, TbAlertTriangle, TbShieldHalf, TbCheck, TbLoader2 } from "react-icons/tb";
import { RATING_LABEL, SOURCE_LABEL } from "../../lib/ratings";
import RatingPicker from "./RatingPicker";
import "./Reviews.css";

// Where a finding becomes the statement a client reads — opened in place, under its row.
//
// This replaced a drawer that slid in from the right over a dimmed page. The drawer hid
// the very table it was editing, squeezed three paragraphs of writing into a 300px column
// with most of the panel left empty, and made "next item" a close-then-click round trip.
// In place, the item keeps its neighbours in view, the three fields sit side by side at a
// width people can actually write in, and moving through a category is one keystroke.
//
// Saving is automatic, a moment after typing stops and again when a field loses focus,
// through the same optimistic rateItem the rating picker uses — so the "Saved" marker in
// the breadcrumb already speaks for it. Editing changes THIS assessment only; the library
// keeps its own wording.

const FIELDS = [
  { key: "summary", label: "Summary", hint: "What is true today, in a sentence or two.", placeholder: "e.g. Six of the twenty workstations are past their warranty and run an unsupported OS." },
  { key: "business_impact", label: "Business impact", hint: "What it costs them if nothing changes.", placeholder: "e.g. A failed machine means a day of lost work, and unpatched systems are an easy way in." },
  { key: "recommendation", label: "Recommendation", hint: "What you would do about it.", placeholder: "e.g. Replace the six oldest machines this quarter and move the rest onto a refresh cycle." },
];

const SAVE_DELAY = 700;

const fromItem = (item) => ({
  summary: item?.summary || "",
  business_impact: item?.business_impact || "",
  recommendation: item?.recommendation || "",
});

const ItemPanel = ({ item, categoryName, position, total, locked, onSave, onPrev, onNext, onClose }) => {
  const [draft, setDraft] = useState(() => fromItem(item));
  const [status, setStatus] = useState("idle"); // idle | pending | saving | saved
  const timer = useRef(null);
  const pending = useRef(null);
  const panelRef = useRef(null);

  // Reset only when the ITEM changes. Resetting whenever its text changed would let a save
  // landing mid-sentence overwrite whatever was typed after it.
  useEffect(() => {
    setDraft(fromItem(item));
    setStatus("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  // Bring the panel into view when it opens or moves to another item.
  useEffect(() => {
    panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    panelRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
  }, [item?.id]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const patch = pending.current;
    if (!patch) return;
    pending.current = null;
    setStatus("saving");
    try {
      await onSave(item.id, patch);
      setStatus(pending.current ? "pending" : "saved");
    } catch {
      setStatus("idle");
    }
  }, [item?.id, onSave]);

  // Whatever is unsaved goes before the panel closes or moves on.
  useEffect(() => () => { if (pending.current) flush(); }, [flush]);

  if (!item) return null;

  const change = (key) => (e) => {
    const value = e.target.value;
    setDraft((d) => ({ ...d, [key]: value }));
    pending.current = { ...(pending.current || {}), [key]: value };
    setStatus("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY);
  };

  const go = (fn) => async () => { await flush(); fn(); };

  // Keyboard: Escape closes, Alt+↑/↓ moves through the category. Bound to the panel, not
  // the window, so it never fights a dialog or the rest of the page.
  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); go(onClose)(); }
    else if (e.altKey && e.key === "ArrowDown" && onNext) { e.preventDefault(); go(onNext)(); }
    else if (e.altKey && e.key === "ArrowUp" && onPrev) { e.preventDefault(); go(onPrev)(); }
  };

  const evidence = item.scan_evidence || [];
  const rated = ["red", "yellow", "green"].includes(item.rating);

  return (
    <section ref={panelRef} className={`v2-ipanel v2-ipanel--${item.rating}`}
             aria-label={`${item.name} write-up`} onKeyDown={onKeyDown}>
      <header className="v2-ipanel-head">
        <div className="v2-ipanel-title">
          <strong>{item.name}</strong>
          <span>
            {categoryName && <>{categoryName} · </>}
            Weight {Number(item.weight).toFixed(1)} · {SOURCE_LABEL[item.source] || item.source}
          </span>
        </div>
        <div className="v2-ipanel-nav">
          {total > 1 && <span className="v2-ipanel-pos">{position} of {total}</span>}
          <button type="button" className="v2-btn v2-btn--icon" aria-label="Previous item" title="Previous item (Alt+↑)"
                  disabled={!onPrev} onClick={go(onPrev)}><TbChevronUp size={15} /></button>
          <button type="button" className="v2-btn v2-btn--icon" aria-label="Next item" title="Next item (Alt+↓)"
                  disabled={!onNext} onClick={go(onNext)}><TbChevronDown size={15} /></button>
          <button type="button" className="v2-btn v2-btn--icon" aria-label="Close" title="Close (Esc)"
                  onClick={go(onClose)}><TbX size={15} /></button>
        </div>
      </header>

      <div className="v2-ipanel-body">
        <aside className="v2-ipanel-side">
          <div className="v2-ipanel-label">Rating</div>
          <RatingPicker value={item.rating} itemName={item.name} disabled={locked}
                        onChange={(rating) => onSave(item.id, { rating })} />
          <p className={`v2-ipanel-verdict${rated ? ` ${item.rating}` : ""}`}>
            {RATING_LABEL[item.rating]}
          </p>
          {item.source === "scan" && (
            <p className="v2-hint">Set from the external scan. Changing it marks the item as consultant rated.</p>
          )}

          {(item.rating === "red" || item.rating === "yellow") && (
            <div className={`v2-info-callout ${item.rating}`}>
              <span className="v2-info-callout-ic"><TbAlertTriangle size={15} /></span>
              <div>
                <small>{item.rating === "red" ? "Immediate attention" : "Needs improvement"}</small>
                <strong>Worth raising in the meeting, not just the report.</strong>
              </div>
            </div>
          )}

          {evidence.length > 0 && (
            <div className="v2-ipanel-evidence">
              <div className="v2-ipanel-label"><TbShieldHalf size={13} /> Evidence from the scan</div>
              {evidence.map((ev, i) => (
                <div key={i} className="v2-srcrow">
                  <span className="v2-srcrow-sev" style={{ background: "var(--v2-rating-red)" }} />
                  <span style={{ flex: 1, minWidth: 0 }}>{ev.title}</span>
                  <span className="v2-dim">{ev.severity}</span>
                </div>
              ))}
            </div>
          )}
        </aside>

        <div className="v2-ipanel-fields">
          {FIELDS.map(({ key, label, hint, placeholder }) => (
            <label key={key} className="v2-ipanel-field">
              <span className="v2-ipanel-field-label">{label}</span>
              <span className="v2-ipanel-field-hint">{hint}</span>
              <textarea className="v2-input" rows={5} value={draft[key]} readOnly={locked}
                        placeholder={locked ? "" : placeholder}
                        onChange={change(key)} onBlur={flush} />
            </label>
          ))}
        </div>
      </div>

      <footer className="v2-ipanel-foot">
        <span>Shown to the client in the report. Changes apply to this assessment only.</span>
        <span className={`v2-ipanel-save ${status}`} aria-live="polite">
          {locked ? "Locked — this review has been presented"
            : status === "saving" ? <><TbLoader2 size={13} className="v2-spin" /> Saving…</>
            : status === "pending" ? "Editing…"
            : status === "saved" ? <><TbCheck size={13} /> Saved</>
            : "Saves automatically"}
        </span>
        <span className="v2-ipanel-keys" aria-hidden="true"><kbd>Alt</kbd>+<kbd>↓</kbd> next · <kbd>Esc</kbd> close</span>
      </footer>
    </section>
  );
};

export default ItemPanel;
